import { Mail, Workflow } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import RecipientPicker from "@/components/RecipientPicker";
import { BlockEditor, EmailPreview, TEMPLATE_BLOCKS } from "@/components/EmailBlocks";
import { DRIP_PRESET, TEMPLATES, TOKEN } from "@/lib/crm";

const Field = ({ label, children, action }) => (
  <div className="space-y-1.5">
    <div className="flex justify-between items-center"><Label>{label}</Label>{action}</div>
    {children}
  </div>
);

const InsertToken = ({ onClick, testId }) => (
  <button type="button" className="text-xs font-semibold text-[#C59B27]" onClick={onClick} data-testid={testId}>+ {TOKEN}</button>
);

export function SetupStep({ f, set, contacts }) {
  return (
    <div className="space-y-5">
      <div className="grid sm:grid-cols-2 gap-3">
        {[["email", Mail, "Email", "One email, sent now or scheduled."], ["drip", Workflow, "Email Sequence", "A series of emails sent automatically over several days."]].map(([v, Icon, t, s]) => (
          <button key={v} type="button" onClick={() => set(v === "drip" ? { type: v, name: f.name || DRIP_PRESET.name, steps: DRIP_PRESET.steps } : { type: v })}
            className={`rounded-xl border-2 p-4 text-left transition-colors ${f.type === v ? "border-[#3B185F] bg-[#F6F2FB]" : "hover:border-[#D4AF37]"}`} data-testid={`campaign-type-${v}`}>
            <Icon className="h-6 w-6 text-[#3B185F]" /><div className="mt-2 font-display text-lg font-bold">{t}</div><p className="text-xs text-stone-500">{s}</p>
          </button>
        ))}
      </div>
      <Field label="Campaign name"><Input value={f.name} onChange={(e) => set({ name: e.target.value })} placeholder="e.g. Spring newsletter" data-testid="campaign-name-input" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="From name"><Input value={f.from_name} onChange={(e) => set({ from_name: e.target.value })} data-testid="campaign-from-name-input" /></Field>
        <Field label="From email"><Input type="email" value={f.from_email} onChange={(e) => set({ from_email: e.target.value })} data-testid="campaign-from-email-input" /></Field>
      </div>
      <Field label="To / Recipients"><RecipientPicker contacts={contacts} value={f.recipient_ids} onChange={(ids) => set({ recipient_ids: ids })} /></Field>
    </div>
  );
}

export function DesignStep({ f, set, preview }) {
  if (f.type === "drip") {
    const upd = (i, p) => set({ steps: f.steps.map((x, j) => (j === i ? { ...x, ...p } : x)) });
    return (
      <div className="space-y-3">
        <div className="flex justify-between items-center"><p className="text-sm text-stone-600">Each email in the sequence is sent the given number of days after activation.</p>
          <Button type="button" size="sm" variant="outline" className="border-[#D4AF37]" onClick={() => set({ steps: DRIP_PRESET.steps })} data-testid="drip-load-preset-btn">Use "AVY Clients #1" copy</Button></div>
        {f.steps.map((s, i) => (
          <div key={i} className="rounded-xl border p-4 space-y-2" data-testid={`drip-step-${i}`}>
            <div className="flex items-center gap-3">
              {i === 0
                ? <span className="text-xs font-semibold text-[#3B185F]">Email 1 · sent when the sequence starts</span>
                : <>
                    <span className="text-xs font-semibold text-[#3B185F]">Email {i + 1}</span>
                    <Label className="text-xs">· sent on day</Label>
                    <Input type="number" min={1} className="w-20 h-8" value={s.day} onChange={(e) => upd(i, { day: Number(e.target.value) })} data-testid={`drip-step-day-${i}`} />
                  </>}
            </div>
            <Field label="Subject" action={<InsertToken onClick={() => upd(i, { subject: `${s.subject} ${TOKEN}`.trim() })} testId={`drip-step-token-${i}`} />}>
              <Input value={s.subject} onChange={(e) => upd(i, { subject: e.target.value })} data-testid={`drip-step-subject-${i}`} /></Field>
            <Textarea rows={3} value={s.body} onChange={(e) => upd(i, { body: e.target.value })} data-testid={`drip-step-body-${i}`} />
          </div>
        ))}
      </div>
    );
  }
  const pickTemplate = (id) => {
    const t = TEMPLATES.find((x) => x.id === id);
    set({ template: id, subject: t.subject, blocks: TEMPLATE_BLOCKS[id]() });
  };
  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <div className="space-y-4">
        <Field label="Start from a template">
          <Select value={f.template} onValueChange={pickTemplate}>
            <SelectTrigger data-testid="campaign-template-select"><SelectValue placeholder="Select a template" /></SelectTrigger>
            <SelectContent>{TEMPLATES.map((t) => <SelectItem key={t.id} value={t.id} data-testid={`template-option-${t.id}`}>{t.name}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Subject" action={<InsertToken onClick={() => set({ subject: `${f.subject} ${TOKEN}`.trim() })} testId="insert-first-name-subject" />}>
          <Input value={f.subject} onChange={(e) => set({ subject: e.target.value })} data-testid="campaign-subject-input" /></Field>
        <Field label="Email content"><BlockEditor blocks={f.blocks} onChange={(blocks) => set({ blocks })} /></Field>
      </div>
      <div className="lg:sticky lg:top-0 h-fit">
        <Label className="mb-1.5 block">Live preview</Label>
        <EmailPreview subject={f.subject} blocks={f.blocks} contact={preview} fromName={f.from_name} fromEmail={f.from_email} />
      </div>
    </div>
  );
}

export function ReviewStep({ f, set, preview, recipientsCount }) {
  return (
    <div className="space-y-5">
      <div className="grid sm:grid-cols-3 gap-3 text-sm">
        {[["Campaign", f.name], ["From", `${f.from_name} <${f.from_email}>`], ["To", `${recipientsCount} recipient${recipientsCount === 1 ? "" : "s"}`]].map(([k, v]) => (
          <div key={k} className="rounded-lg border p-3"><div className="text-[10px] uppercase tracking-wider text-stone-500">{k}</div><div className="font-semibold text-[#19082B] truncate">{v}</div></div>
        ))}
      </div>
      <div data-testid="campaign-preview">
        <div className="text-[10px] uppercase tracking-widest text-[#7a5c0c] font-bold mb-2">Preview · as seen by {preview?.first_name || "the recipient"}{f.type === "drip" && " · Email 1"}</div>
        {f.type === "drip"
          ? <EmailPreview subject={f.steps[0]?.subject} body={f.steps[0]?.body} contact={preview} fromName={f.from_name} fromEmail={f.from_email} />
          : <EmailPreview subject={f.subject} blocks={f.blocks} contact={preview} fromName={f.from_name} fromEmail={f.from_email} />}
      </div>
      <div className="space-y-3">
        <Label>Send options</Label>
        <RadioGroup value={f.mode} onValueChange={(v) => set({ mode: v })} className="flex flex-wrap gap-4">
          {[["now", f.type === "drip" ? "Activate now" : "Send now"], ["schedule", "Schedule later"], ["draft", "Save as draft"]].map(([v, l]) => (
            <label key={v} className="flex items-center gap-2 text-sm"><RadioGroupItem value={v} data-testid={`campaign-mode-${v}`} />{l}</label>
          ))}
        </RadioGroup>
        {f.mode === "schedule" && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Select date"><Input type="date" value={f.date} onChange={(e) => set({ date: e.target.value })} data-testid="campaign-schedule-date" /></Field>
            <Field label="Select time"><Input type="time" value={f.time} onChange={(e) => set({ time: e.target.value })} data-testid="campaign-schedule-time" /></Field>
          </div>
        )}
      </div>
    </div>
  );
}
